import { useRouter } from 'next/router'
import { User } from '@supabase/auth-helpers-react'
import { useCallback, useEffect, useState } from 'react'
import { PATHS } from './const'
import { supabaseClient } from './supabase'
import { localAPIRequest } from './request'

export function classNames(...classes: any[]): string {
  return classes.filter(Boolean).join(' ')
}

export function createNoteLinkWithID(
  note_id: string,
  postFix: string | '' | 'note' | 'recipients' | 'settings' | 'activate' = ''
) {
  return `${PATHS.SINGLE_NOTE_INDEX}${note_id}/${postFix}`
}

export function createRecipientLinkWithID(
  note_id: string,
  recipient_id: string,
  postFix?: string | 'new'
) {
  return `${PATHS.SINGLE_NOTE_INDEX}${note_id}/recipients/${
    postFix ?? recipient_id
  }`
}

type formatDateOptions = {
  year?: 'numeric'
  month?: 'long'
  weekday?: 'long'
  hour12?: boolean
  day?: 'numeric'
  hour?: 'numeric'
  minute?: 'numeric'
}

// https://css-tricks.com/how-to-convert-a-date-string-into-a-human-readable-format/
export const formatDate = (
  dateString: string | null | undefined,
  formatOptions: formatDateOptions = {
    year: 'numeric',
    month: 'long',
    weekday: 'long',
    hour12: true,
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }
) => {
  // @ts-ignore
  return new Date(dateString).toLocaleDateString(undefined, formatOptions)
}

export const debounce = (fn: Function, delay: number) => {
  let timeoutID: NodeJS.Timeout

  return function (...args: any[]) {
    if (timeoutID) {
      clearTimeout(timeoutID)
    }
    timeoutID = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}

export const slugify = (str = '') => {
  return str.replace(/[^\w]+/g, '-').toLowerCase()
}

export function useRequireAuth(
  doAuth = true,
  redirectUrl = PATHS.AFTER_SIGN_OUT
) {
  const [currentUser, setUser] = useState<User | null>(null)
  const router = useRouter()

  const checkAndRedirect = useCallback(
    debounce(async () => {
      const {
        data: { user },
      } = await supabaseClient.auth.getUser()

      // If user.id is false that means we're not
      // logged in and should redirect.
      if (doAuth && !user?.id) {
        router.push({
          pathname: redirectUrl,
          query: { next: router.asPath },
        })
        return
      }

      setUser(user)
    }, 1000),
    []
  )

  useEffect(() => {
    if (!router.isReady) return

    checkAndRedirect()
  }, [router.isReady])

  return currentUser
}

export const changeTheme = () => {
  if (localStorage.getItem('color-theme')) {
    if (localStorage.getItem('color-theme') === 'light') {
      document.documentElement.classList.add('dark')
      localStorage.setItem('color-theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('color-theme', 'light')
    }
  } else {
    if (document.documentElement.classList.contains('dark')) {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('color-theme', 'light')
    } else {
      document.documentElement.classList.add('dark')
      localStorage.setItem('color-theme', 'dark')
    }
  }
}

export const setCurrentTheme = () => {
  if (
    localStorage.getItem('color-theme') === 'dark' ||
    (!('color-theme' in localStorage) &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  ) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

/**
 * Convert a date to a relative time string, such as
 * "a minute ago", "in 2 hours", "yesterday", "3 months ago", etc.
 * using Intl.RelativeTimeFormat
 * credit: https://dev.to/builderio/fast-and-light-relative-time-strings-in-js-khg
 */
export function getRelativeTimeString(
  date: Date | number,
  lang = navigator.language
): string {
  // Allow dates or times to be passed
  const timeMs = typeof date === 'number' ? date : date.getTime()

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000)

  // Array reprsenting one minute, hour, day, week, month, etc in seconds
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * 30,
    86400 * 365,
    Infinity,
  ]

  // Array equivalent to the above but in the string representation of the units
  const units: Intl.RelativeTimeFormatUnit[] = [
    'second',
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'year',
  ]

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds)
  )

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex])
}

export function logEvent(eventName: string) {
  return localAPIRequest({
    action: 'logEvent',
    data: { event_name: eventName },
  })
}

export async function getCountry() {
  const response = await localAPIRequest({
    action: '',
    method: 'get',
    withAuth: false,
    endpoint: '/api/loc',
  })

  return response.data
}
